import { mergeSxProps, Text } from "@clipboard-health/ui-components";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type ReactElement, useEffect, useLayoutEffect, useRef, useState } from "react";

import { CreditProgressBar, type CreditProgressBarProps } from "./CreditProgressBar";

// TODO: The tooltip timing and sizing are tightly coupled to CreditProgressBar internals.
// This component should be revisited — likely folded into CreditProgressBar itself.
// See https://linear.app/clipboardhealth/issue/SYNF-182/consolidate-progress-bar-components

// Default duration for the progress fill animation in milliseconds (must match CreditProgressBar fill mode)
const DEFAULT_ANIMATION_DURATION_MS = 400;
// Delay before starting the animation (must match CreditProgressBar)
const ANIMATION_DELAY_MS = 100;
// Additional delay before showing tooltip after animation completes
const TOOLTIP_DELAY_MS = 200;

// Circle size from CreditProgressBar - the overlap affects tooltip positioning
const CIRCLE_SIZE = 52;

/** Height reserved above the progress bar for the tooltip bubble + pointer */
const TOOLTIP_RESERVED_HEIGHT = "28px";

export interface CreditProgressBarWithTooltipProps extends CreditProgressBarProps {
  /**
   * Whether to show the tooltip with progress count
   * @default false
   */
  showTooltip?: boolean;
  /**
   * Custom sx prop for the container
   */
  containerSx?: SxProps<Theme>;
}

export function CreditProgressBarWithTooltip(
  props: CreditProgressBarWithTooltipProps
): ReactElement {
  const {
    completedCount,
    requiredCount,
    color = "blue",
    showTooltip = false,
    containerSx,
    ...progressBarProps
  } = props;

  const progress = requiredCount > 0 ? Math.max(0, Math.min(completedCount / requiredCount, 1)) : 0;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipLeftPx, setTooltipLeftPx] = useState(0);

  // Calculate tooltip position based on container width and progress
  useLayoutEffect(() => {
    if (!showTooltip || !containerRef.current) {
      return;
    }

    const calculatePosition = (): void => {
      if (!containerRef.current) {
        return;
      }

      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth === 0) {
        return;
      }

      // The visible progress bar area is the container minus the full circle width.
      // The circle (52px) overlaps the pill by 26px, but we want the tooltip to stay
      // within the visible fill area (not go under the circle).
      const visibleProgressWidth = containerWidth - CIRCLE_SIZE;

      // Tooltip center should align with where the progress fill ends
      const targetPosition = visibleProgressWidth * progress;

      setTooltipLeftPx(targetPosition);
    };

    // Use requestAnimationFrame to ensure DOM is laid out
    const rafId = requestAnimationFrame(calculatePosition);

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(calculatePosition);
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [progress, showTooltip]);

  // Show tooltip after animation completes
  useEffect(() => {
    if (!showTooltip) {
      setTooltipVisible(false);
      return;
    }

    const effectiveDuration = DEFAULT_ANIMATION_DURATION_MS;
    const tooltipTimer = setTimeout(
      () => {
        setTooltipVisible(true);
      },
      ANIMATION_DELAY_MS + effectiveDuration + TOOLTIP_DELAY_MS
    );

    return () => {
      clearTimeout(tooltipTimer);
    };
  }, [showTooltip]);

  return (
    <Box
      ref={containerRef}
      sx={mergeSxProps(
        {
          position: "relative",
          width: "100%",
          // Reserve space for tooltip when it's supposed to show
          paddingTop: showTooltip ? TOOLTIP_RESERVED_HEIGHT : 0,
        },
        containerSx
      )}
    >
      {/* Tooltip bubble - centered on the progress end position */}
      {showTooltip && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: tooltipLeftPx,
            transform: "translateX(-50%)",
            opacity: tooltipVisible ? 1 : 0,
            transition: "opacity 300ms ease-out",
          }}
        >
          {/* Bubble container */}
          <Box
            sx={(theme) => ({
              background: theme.palette.text.primary,
              borderRadius: "8px",
              padding: "6px 10px",
              boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.25)",
              position: "relative",
            })}
          >
            <Text
              semibold
              variant="caption"
              color={(theme) => theme.palette.text.invertPrimary}
              sx={{ whiteSpace: "nowrap" }}
            >
              {completedCount}/{requiredCount}
            </Text>
            {/* Triangle pointer - always centered on bubble */}
            <Box
              sx={(theme) => ({
                position: "absolute",
                bottom: -6,
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: `6px solid ${theme.palette.text.primary}`,
              })}
            />
          </Box>
        </Box>
      )}

      <CreditProgressBar
        completedCount={completedCount}
        requiredCount={requiredCount}
        color={color}
        {...progressBarProps}
      />
    </Box>
  );
}
